import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Input,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  DatePicker,
  Table,
  Tag,
  Popconfirm,
  Typography,
  Tooltip,
  Divider,
  Tabs,
  Modal,
  Checkbox,
  Space,
  Spin,
} from "antd";
import moment from "moment";
import Page from "_components/Page";
import {
  jobService,
  materialDetailService,
  stockIssueService,
} from "_services";
import { SearchOutlined } from "@ant-design/icons";
import StockSelection from "./StockSelection";

function IssueToJob({ history, match }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAvailableStocksLoading, setIsAvailableStocksLoading] =
    useState(false);
  const [isStockSelectionVisible, setIsStockSelectionVisible] = useState(false);
  const [isIssueItemsLoading, setIsIssueItemsLoading] = useState(false);
  const [issuedLines, setIssuedLines] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedWorkItem, setSelectedWorkItem] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedMaterialLineKeys, setSelectedMaterialLineKeys] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState(null);
  const [availableStocks, setAvailableStocks] = useState([]);
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [selectedStockMap, setSelectedStockMap] = useState(new Map());
  const [selectedMaterialLine, setSelectedMaterialLine] = useState(null);
  const [materialLines, setMaterialLines] = useState([]);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();
  const { Text } = Typography;

  useEffect(() => {
    getJobs();
  }, []);

  const getJobs = () => {
    setIsLoading(true);
    jobService
      .search("?jobStatus=OPEN")
      .then((x) => {
        setIsLoading(false);
        setOpenJobs(x.content);
      })
      .catch((errorInfo) => {
        setIsLoading(false);
        message.error(`${errorInfo}`);
      });
  };

  const handleJobChange = (value) => {
    setSelectedJob(null);
    setSelectedWorkItem(null);
    setSelectedTask(null);
    const job = openJobs.find((item) => item.id === value);
    if (job) {
      setSelectedJob(job);
    }
  };

  const handleWorkItemChange = (value) => {
    setSelectedWorkItem(null);
    setSelectedTask(null);
    const workItem =
      selectedJob &&
      selectedJob.workItems &&
      selectedJob.workItems.find((item) => item.id === value);
    if (workItem) {
      setSelectedWorkItem(workItem);
    }
  };

  const handleTaskChange = (value) => {
    const task =
      selectedWorkItem &&
      selectedWorkItem.jobTasks &&
      selectedWorkItem.jobTasks.find((item) => item.id === value);
    if (task) {
      task.materials.forEach((item) => {
        item.key = item.material.id;
      });
      setSelectedTask(task);
      setMaterialLines(task.materials);
    }
  };

  const getActionButtons = () => {
    let buttons = [];
    buttons.push(
      <Popconfirm
        title="Are you sure to issue these stocks?"
        onConfirm={(e) => {
          e.preventDefault();
          handleFormSumbit();
        }}
        key="save-confirm"
        disabled={
          issuedLines
            .flatMap((line) => line.issuedItems)
            .filter((item) => item.added).length === 0
        }
      >
        <Button
          key="btn-save"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={
            issuedLines
              .flatMap((line) => line.issuedItems)
              .filter((item) => item.added).length === 0
          }
        >
          Save
        </Button>
      </Popconfirm>
    );
    buttons.push(
      <Button key="btn-cancel" htmlType="button" onClick={onCancel}>
        Cancel
      </Button>
    );

    return buttons;
  };

  const materialLineRowSelection = {
    selectedRowKeys: selectedMaterialLineKeys,
    onChange: (selectedMaterialLineKeys) => {
      console.log("Selected:", selectedMaterialLineKeys);
      handleTabChange(selectedMaterialLineKeys[0]);
    },
  };

  const handleTabChange = (activeKey) => {
    console.log("Active:", activeKey);
    setActiveTabKey(activeKey);
    setSelectedMaterialLineKeys([activeKey]);
    // console.log("Active:", activeKey);
  };

  const handleSearchAndAddStocks = (line) => {
    handleTabChange(line.key);
    const selectedStockTypeId = line.material.id;
    setSelectedMaterialLine(line);

    if (selectedStockTypeId) {
      getStocks(selectedStockTypeId);
    } else {
      message.error("Please select valid material type!");
    }
  };

  const getStocks = (stockTypeId) => {
    let searchParams = `?stockTypeId=${stockTypeId}&mode=AVAILABLE&pageNumber=1&pageSize=1000`;

    setIsAvailableStocksLoading(true);
    materialDetailService
      .searchAvailable(searchParams)
      .then((data) => {
        setIsAvailableStocksLoading(false);
        if (data.content.length > 0) {
          data.content.forEach((item) => {
            item.issueQuantity = item.quantity;
          });
          setAvailableStocks(data.content);
          setIsStockSelectionVisible(true);
        } else {
          setAvailableStocks([]);
          message.info(
            "Sorry, There's no available stocks for the given criteria!"
          );
        }
      })
      .catch((error) => {
        setIsAvailableStocksLoading(false);
        message.error(`${error}`);
      });
  };

  const handleStockSelection = (stockIds) => {
    setSelectedStockIds(stockIds);
  };

  const handleStockQuantitySelection = (stockId, quantity) => {
    setSelectedStockMap(selectedStockMap.set(stockId, quantity));
    console.log("Quantity Change: ", selectedStockMap);
  };

  const handleAddStocks = () => {
    //console.log("Selected: ", selectedStockIds);
    let selectedStocks = availableStocks.filter((item) =>
      selectedStockIds.includes(item.id)
    );

    //console.log("SelectedST: ", materialRequestLines);
    setIsIssueItemsLoading(true);
    if (selectedStocks) {
      selectedStocks.forEach((item) => {
        if (selectedStockMap.get(item.id)) {
          item.issueQuantity = selectedStockMap.get(item.id);
        }
      });
      materialLines.forEach((line) => {
        if (line.key === activeTabKey) {
          line.issueItems = selectedStocks;
          line.issueQuantity = selectedStocks
            .map((item) => item.issueQuantity)
            .reduce((prev, next) => prev + next);
        }
      });

      //setRequestedStocks([...requestedStocks, ...selectedStocks]);
      //console.log("RequestedStocks:", requestedStocks);
    }
    //form.setFieldsValue({ stockType: null, lotNumber: null });
    setIsStockSelectionVisible(false);
    setIsIssueItemsLoading(false);
    setSelectedStockIds([]);
    //console.log("requestLines:", materialRequestLines);
  };

  const getTabContent = (lines) => {
    let tabPanes = [];
    lines.forEach((line, index) => {
      if (line.issueItems && line.issueItems.length > 0) {
        tabPanes.push(
          <Tabs.TabPane
            tab={`${line.material && line.material.code} (${
              line.issueItems && line.issueItems.length
            })`}
            key={index}
          >
            <Table
              bordered
              size="small"
              rowKey="id"
              columns={getStockItemColumns()}
              dataSource={line.issueItems}
              pagination={false}
              summary={(pageData) => {
                let totalQuantity = 0;
                let totalIssueQuantity = 0;

                pageData.forEach(({ quantity, issueQuantity }) => {
                  totalQuantity += quantity;
                  totalIssueQuantity += issueQuantity;
                });

                return (
                  <>
                    <Table.Summary.Row>
                      <Table.Summary.Cell colSpan={4}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <Text strong>{line.issueItems.length}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <Text strong>{totalQuantity}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell align="right">
                        <Text strong>
                          {Number(totalIssueQuantity).toFixed(3)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell colSpan={2} />
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
          </Tabs.TabPane>
        );
      }
    });

    return tabPanes;
  };

  const onCancel = () => {
    history.push(".");
  };

  const handleFormSumbit = () => {
    form
      .validateFields()
      .then((values) => {
        values.store = selectedJob.store;
        values.storeAddress = selectedJob.storeAddress;
        values.customer = selectedJob.store;
        /* values.orderStage = isComplete ? "ISSUED" : "PROCESSING";
                values.issuedDate = moment(values.issuedDate).format(
                  "YYYY-MM-DD HH:mm:ss"
                );
                values.issuedLines = issuedLines.map((line) => ({
                  requestLine: { id: line.requestLine.id },
                  quantity: line.issuedItems.filter((item) => item.added).length,
                  stockType: { id: line.stockType.id },
                  lotNumber: { id: line.lotNumber && line.lotNumber.id },
                  issuedItems: line.issuedItems
                    .filter((item) => (isComplete ? item.loaded : item.added))
                    .map((item) => ({
                      id: item.id,
                      mode: isComplete ? "ISSUED" : item.mode,
                    })),
                })); */

        // console.log("OUT: ", values);
        //console.log("OUT: ", JSON.stringify(values));
        stockIssueService
          .create(values)
          .then((data) => {
            history.push(`/stock-issue/${data.content.id}/edit`);
          })
          .catch((error) => {
            setError(error);
          });
      })
      .catch((error) => {
        //message.error(`${error}`);
      });
  };

  const getLineColumns = () => {
    const lineColumns = [];
    lineColumns.push({
      title: "Material Type",
      key: "materialCode",
      render: (text, record) => {
        return record.material && record.material.code;
      },
    });
    lineColumns.push({
      title: "Description",
      key: "materialName",
      width: "30%",
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        return (
          <Tooltip placeholder="topLeft" title={record.material.name}>
            {record.material.name}
          </Tooltip>
        );
      },
    });
    lineColumns.push({
      title: "Required",
      dataIndex: "requiredQuantity",
      key: "requiredQuantity",
    });
    lineColumns.push({
      title: "Issued",
      dataIndex: "issuedQuantity",
      key: "issuedQuantity",
    });
    lineColumns.push({
      title: "Issue Quantity",
      dataIndex: "issueQuantity",
      key: "issueQuantity",
      render: (text, record) => {
        return (
          record.issueItems &&
          record.issueItems
            .map((item) => item.issueQuantity)
            .reduce((prev, next) => prev + next)
        );
      },
    });
    lineColumns.push({
      title: "",
      key: "operation",
      render: (text, record) => {
        return (
          <Space size="small">
            <Button
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleSearchAndAddStocks(record);
              }}
              icon={<SearchOutlined />}
              loading={isAvailableStocksLoading && record.key === activeTabKey}
            >
              Find Stocks
            </Button>
          </Space>
        );
      },
    });

    return lineColumns;
  };

  const getStockItemColumns = () => {
    let columns = [];
    columns.push({
      title: "Material",
      dataIndex: "stockType",
      key: "stockType",
      render: (text, record) => {
        return record.stockType && record.stockType.code;
      },
    });
    columns.push({
      title: "Description",
      key: "stockTypeName",
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        return (
          <Tooltip placeholder="topLeft" title={record.stockType.name}>
            {record.stockType.name}
          </Tooltip>
        );
      },
    });
    columns.push({
      title: "Recv Date",
      dataIndex: "receivedDate",
      key: "receivedDate",
    });
    columns.push({
      title: "Bin",
      key: "stockBin",
      render: (text, record) => {
        return record.stockBin && record.stockBin.code;
      },
    });
    columns.push({
      title: "Pallet #",
      dataIndex: "palletNo",
      key: "palletNo",
      align: "right",
    });
    columns.push({
      title: "Quantity",
      dataIndex: "quantity",
      key: "issueQuantity",
      align: "right",
    });
    columns.push({
      title: "Issue Quantity",
      dataIndex: "issueQuantity",
      key: "issueQuantity",
      align: "right",
    });
    columns.push({
      title: "Receipt Type",
      dataIndex: "receiptType",
      key: "receiptType",
      render: (text) => {
        let color = "";
        if (text === "TIEP") {
          color = "#87d068";
        }
        if (text === "NON_TIEP") {
          color = "#2db7f5";
        }
        if (text === "LOCAL") {
          color = "#108ee9";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    });
    columns.push({
      title: "Status",
      dataIndex: "mode",
      key: "mode",
      render: (text) => {
        let color = "";
        if (text === "AVAILABLE") {
          color = "#87d068";
        }
        if (text === "REQUESTED") {
          color = "#2db7f5";
        }
        if (text === "ISSUED") {
          color = "#108ee9";
        }
        if (text === "DELIVERED") {
          color = "#f50";
        }
        if (text === "LOADED") {
          color = "#87d068";
        }
        if (text === "READY") {
          color = "#faad14";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    });

    return columns;
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/stock-issue">Issued Stocks Search</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/stock-issue/add">Issue Stocks</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/stock-issue/issue-to-job">Issue To Job</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <Spin tip="Loading..." spinning={isLoading}>
            <Form
              layout="vertical"
              form={form}
              name="form-create"
              requiredMark={false}
            >
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item
                    name="jobId"
                    label="Job"
                    rules={[
                      {
                        required: true,
                        message: "Job is required",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Please select"
                      allowClear
                      optionFilterProp="children"
                      showSearch
                      onChange={(e) => {
                        form.setFieldsValue({ workItemId: null });
                        form.setFieldsValue({ taskId: null });
                        //setSelectedJob(e);
                        handleJobChange(e);
                      }}
                      onClear={() => {
                        form.setFieldsValue({ workItemId: null });
                        form.setFieldsValue({ taskId: null });
                      }}
                    >
                      {openJobs.length > 0 &&
                        openJobs.map((t) => (
                          <Select.Option value={t.id} key={t.id}>
                            ({t.code}){t.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="workItemId"
                    label="Work Item"
                    rules={[
                      {
                        required: true,
                        message: "Work Item is required",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Please select"
                      allowClear
                      optionFilterProp="children"
                      showSearch
                      onChange={(e) => {
                        form.setFieldsValue({ taskId: null });
                        //setSelectedWorkItemId(e);
                        handleWorkItemChange(e);
                      }}
                      onClear={() => {
                        form.setFieldsValue({ taskId: null });
                      }}
                      disabled={!selectedJob}
                    >
                      {selectedJob &&
                        selectedJob.workItems.length > 0 &&
                        selectedJob.workItems.map((t) => (
                          <Select.Option value={t.id} key={t.id}>
                            ({t.code}){t.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="taskId"
                    label="Task"
                    rules={[
                      {
                        required: true,
                        message: "Task is required",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Please select"
                      allowClear
                      optionFilterProp="children"
                      showSearch
                      onChange={handleTaskChange}
                      disabled={!selectedWorkItem}
                    >
                      {selectedWorkItem &&
                        selectedWorkItem.jobTasks.length > 0 &&
                        selectedWorkItem.jobTasks.map((t) => (
                          <Select.Option value={t.id} key={t.id}>
                            ({t.code}){t.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="issuedDate"
                    label="Issue Date"
                    rules={[
                      { required: false, message: "Issue date is required" },
                    ]}
                    initialValue={moment()}
                  >
                    <DatePicker
                      placeholder="Issue date"
                      format="YYYY-MM-DD"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {materialLines.length > 0 && (
                <Table
                  bordered
                  pagination={false}
                  //rowKey="id"
                  size="small"
                  columns={getLineColumns()}
                  dataSource={materialLines}
                  rowSelection={{
                    type: "radio",
                    ...materialLineRowSelection,
                  }}
                />
              )}

              {materialLines.length > 0 &&
                materialLines.flatMap((item) => item.issueItems).length > 0 && (
                  <>
                    <Divider orientation="left">Stock Items</Divider>
                    <Spin tip="Loading..." spinning={isIssueItemsLoading}>
                      <Tabs defaultActiveKey={materialLines[0].key}>
                        {getTabContent(materialLines)}
                      </Tabs>
                    </Spin>
                  </>
                )}
            </Form>
            <Modal
              title={`Stocks (${availableStocks.length}) - ${
                selectedMaterialLine && selectedMaterialLine.material.code
              }`}
              style={{ top: 48 }}
              visible={isStockSelectionVisible}
              okText={`Add Stocks (${selectedStockIds.length})`}
              onOk={handleAddStocks}
              //okButtonProps={{ disabled: selectedStockIds.length === 0 }}
              onCancel={() => {
                setSelectedStockIds([]);
                setIsStockSelectionVisible(false);
              }}
              width={1300}
              destroyOnClose={true}
            >
              <StockSelection
                stocks={availableStocks}
                selectedIds={selectedStockIds}
                onSelectChange={handleStockSelection}
                onQuantityChange={handleStockQuantitySelection}
              />
            </Modal>
          </Spin>
        </Page>
      </PageHeader>
    </div>
  );
}

export default IssueToJob;
